import React, { useRef, useState, useEffect } from "react";
import styles from "./BlockModal.module.scss";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const BlockModal = ({ onClose, onBlock, initialSport, availableSports }) => {
  const [sport, setSport] = useState(initialSport || "");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState(1);
  const [courtType, setCourtType] = useState("");
  const [noOfCourts, setNoOfCourts] = useState(1);
  const [isTimeDropdownOpen, setIsTimeDropdownOpen] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [maxCourts, setMaxCourts] = useState(1);
  const datePickerRef = useRef(null);
  const timeDropdownRef = useRef(null);

  // Set maxDate to 6 days from today
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 6);

  // Format date to YYYY-MM-DD
  const formatDate = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Fetch available slots when sport, date, and duration change
  useEffect(() => {
    const fetchAvailableSlots = async () => {
      if (sport && date && duration) {
        try {
          const token = localStorage.getItem("access_token");
          const formattedDate = formatDate(date);
          const response = await fetch(
            `https://ballersapi.ballerslife.co/api/v1/facility_admin/bookings/get_slots?date=${formattedDate}&sport=${sport}&duration=${duration}`,
            {
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const data = await response.json();
          setAvailableSlots(data.available_slots || []);
          // Reset time and courts when new slots are fetched
          setTime("");
          setNoOfCourts(1);
          setMaxCourts(1);
        } catch (error) {
          console.error("Error fetching available slots:", error);
          setAvailableSlots([]);
          setTime("");
          setNoOfCourts(1);
          setMaxCourts(1);
        }
      }
    };

    fetchAvailableSlots();
  }, [sport, date, duration]);

  // Update max courts when time is selected
  useEffect(() => {
    if (time && availableSlots.length > 0) {
      const selectedSlot = availableSlots.find(
        (slot) => slot.start_time === time
      );
      if (selectedSlot) {
        setMaxCourts(selectedSlot.available_count);
        // Ensure noOfCourts doesn't exceed available_count
        if (noOfCourts > selectedSlot.available_count) {
          setNoOfCourts(selectedSlot.available_count);
        }
      }
    }
  }, [time, availableSlots, noOfCourts]);

  const handleDateChange = (newDate) => {
    setDate(newDate);
    setTime(""); // Reset time when date changes
  };

  const handleTimeSelect = (selectedTime) => {
    setTime(selectedTime);
    setIsTimeDropdownOpen(false);
  };

  const handleBlock = () => {
    const formattedDate = formatDate(date);
    const blockData = {
      sport,
      date: formattedDate,
      time,
      duration,
      courtType,
      noOfCourts,
    };
    onBlock(blockData);
  };

  // Handle clicks outside time dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        timeDropdownRef.current &&
        !timeDropdownRef.current.contains(event.target)
      ) {
        setIsTimeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Validate required fields (restored to original logic)
  const isBlockDisabled = !sport || !date;

  return (
    <div className={styles.modal}>
      <div className={styles["modal-content-block"]}>
        <h3>Block Slot</h3>
        <button className={styles["close-btn"]} onClick={onClose}>
          ×
        </button>
        <div className={styles["form-group"]}>
          <label>Sport</label>
          <select
            value={sport}
            onChange={(e) => setSport(e.target.value)}
            disabled={availableSports.length === 0}
          >
            {availableSports.length === 0 ? (
              <option value="">No sports available</option>
            ) : (
              <>
                <option value="">Select a sport</option>
                {availableSports.map((sportItem) => (
                  <option key={sportItem} value={sportItem}>
                    {sportItem.charAt(0).toUpperCase() + sportItem.slice(1)}
                  </option>
                ))}
              </>
            )}
          </select>
        </div>
        <div className={styles["form-group"]}>
          <label>Date</label>
          <div className={styles["input-wrapper"]}>
            <DatePicker
              selected={date}
              onChange={handleDateChange}
              dateFormat="dd/MM/yyyy"
              minDate={new Date()}
              maxDate={maxDate}
              className={styles["date-picker"]}
              ref={datePickerRef}
              placeholderText="Select date"
            />
            <span
              className={styles["calendar-icon"]}
              onClick={() => datePickerRef.current.setOpen(true)}
            />
          </div>
        </div>
        <div className={styles["form-group"]}>
          <label>Duration</label>
          <div className={styles["duration-control"]}>
            <button
              onClick={() => setDuration(Math.max(1, duration - 1))}
              disabled={duration <= 1}
            >
              -
            </button>
            <span>{duration} Hour</span>
            <button onClick={() => setDuration(duration + 1)}>+</button>
          </div>
        </div>
        <div className={styles["form-group"]}>
          <label>Start Time</label>
          <div className={styles["time-picker"]} ref={timeDropdownRef}>
            <input
              type="text"
              value={time}
              onClick={() => setIsTimeDropdownOpen(!isTimeDropdownOpen)}
              readOnly
              className={styles["time-input"]}
              placeholder={
                availableSlots.length > 0 ? "Select time" : "No available slots"
              }
              disabled={!sport || !date || availableSlots.length === 0}
            />
            {isTimeDropdownOpen && availableSlots.length > 0 && (
              <div className={styles["time-dropdown"]}>
                {availableSlots.map((slot) => (
                  <div
                    key={slot.start_time}
                    className={styles["time-option"]}
                    onClick={() => handleTimeSelect(slot.start_time)}
                  >
                    {slot.start_time}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className={styles["form-group"]}>
          <label>Court Type</label>
          <input
            type="text"
            value={courtType}
            onChange={(e) => setCourtType(e.target.value)}
            placeholder="Enter court type"
          />
        </div>
        <div className={styles["form-group"]}>
          <label>No of Courts</label>
          <div className={styles["court-control"]}>
            <button
              onClick={() => setNoOfCourts(Math.max(1, noOfCourts - 1))}
              disabled={noOfCourts <= 1 || !time}
            >
              -
            </button>
            <span>{noOfCourts}</span>
            <button
              onClick={() => setNoOfCourts(Math.min(maxCourts, noOfCourts + 1))}
              disabled={noOfCourts >= maxCourts || !time}
            >
              +
            </button>
          </div>
        </div>
        <button
          className={styles["block-now-btn"]}
          onClick={handleBlock}
          disabled={isBlockDisabled}
        >
          Block Now!
        </button>
      </div>
    </div>
  );
};

export default BlockModal;
